import { Children, isValidElement, type ReactElement } from 'react';
import { useRef, useState } from 'react';

import { createRequiredContext } from '../../../../utils/createRequiredContext';
import {
    type CustomSortFunction,
    DATA_TABLE_SIZE,
    type DataTableSize,
    type Query,
    type ReactElementWithPayloadProp,
    SORT_TYPES,
    type SortQuery,
} from '../types';

type DataTableContextValue = SortQuery & {
    size: DataTableSize;
};

type DataTableContextProviderProps = {
    children: React.ReactNode;
    size?: DataTableSize;
    initialSortQuery?: Query;
};

const [useDataTableContext, ContextProvider] = createRequiredContext<DataTableContextValue>('DataTableContext');

/**
 * Extract payload from an element, checking direct props first, then single child.
 * This allows DataTableRow to be wrapped with components like ContextMenu
 * while still being detected and sorted correctly.
 */
export const getPayloadFromElement = (element: ReactElement): Record<string, unknown> | undefined => {
    const props = element.props as Record<string, unknown>;

    if ('payload' in props && props.payload !== undefined) {
        return props.payload as Record<string, unknown>;
    }

    if ('children' in props) {
        const childArray = Children.toArray(props.children as React.ReactNode);
        if (childArray.length === 1 && isValidElement(childArray[0])) {
            const childProps = childArray[0].props as Record<string, unknown>;
            if ('payload' in childProps) {
                return childProps.payload as Record<string, unknown>;
            }
        }
    }

    return undefined;
};

const DataTableContextProvider = ({
    children,
    initialSortQuery,
    size = DATA_TABLE_SIZE.DEFAULT,
}: DataTableContextProviderProps) => {
    const [sortQuery, setSortQuery] = useState<Query>(initialSortQuery ?? { key: '', type: 'NO_SORTING' });
    const customSortFunctions = useRef<Record<string, CustomSortFunction>>({});

    const setCustomSortFunction = (key: string, fc: CustomSortFunction) => {
        customSortFunctions.current[key] = fc;
    };

    const sortStrings = (a: string, b: string): number => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    };

    const sort = (a: ReactElementWithPayloadProp, b: ReactElementWithPayloadProp): number => {
        const aPayload = getPayloadFromElement(a);
        const bPayload = getPayloadFromElement(b);

        let aValue = aPayload?.[sortQuery.key] ?? -1;
        let bValue = bPayload?.[sortQuery.key] ?? -1;

        if (typeof aValue !== typeof bValue) return 0;

        if (typeof aValue === 'object') aValue = JSON.stringify(aValue);
        if (typeof bValue === 'object') bValue = JSON.stringify(bValue);

        if (typeof aValue === 'string' || typeof bValue === 'string') {
            const stringA = typeof aValue === 'string' ? aValue : '';
            const stringB = typeof bValue === 'string' ? bValue : '';
            if (sortQuery.type === SORT_TYPES.ASC) return sortStrings(stringA, stringB);
            return sortStrings(stringB, stringA);
        }

        if (aValue instanceof Date) aValue = aValue.getTime();
        if (bValue instanceof Date) bValue = bValue.getTime();

        if (typeof aValue === 'boolean') aValue = aValue ? 1 : 0;
        if (typeof bValue === 'boolean') bValue = bValue ? 1 : 0;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortQuery.type === SORT_TYPES.ASC ? aValue - bValue : bValue - aValue;
        }

        return 0;
    };

    const sortTableRows = (rows: ReactElementWithPayloadProp[]) => {
        if (customSortFunctions.current[sortQuery.key])
            return customSortFunctions.current[sortQuery.key](rows, sortQuery.type);
        if (sortQuery.type === SORT_TYPES.NO_SORTING) return rows;
        return rows.sort(sort);
    };

    return (
        <ContextProvider
            value={{
                setCustomSortFunction,
                setSortQuery,
                size,
                sortQuery,
                sortTableRows,
            }}
        >
            {children}
        </ContextProvider>
    );
};

export { DataTableContextProvider, useDataTableContext };
